import { createTheme, Theme, ThemeOptions } from '@material-ui/core/styles'
import { Overrides } from '@material-ui/core/styles/overrides'
import { mergeDeepRight } from 'rambdax'
import { UnknownTenantUuidError } from './unknownTenantUuidError'

export type OverrideFactory = (theme: Theme) => Overrides

declare module '@material-ui/core/styles/createPalette' {
  interface Palette {
    tertiary: PaletteColor
  }
}

type ThemeOptionsWithoutOverrides = Omit<ThemeOptions, 'overrides'>

export interface GetThemeOptions {
  overrides?: OverrideFactory | Overrides
  options?: ThemeOptionsWithoutOverrides
}

export type GetTheme = (getThemeOptions: GetThemeOptions) => Theme

export const getThemeFunc =
  (
    defaultThemeOptions: ThemeOptionsWithoutOverrides,
    defaultOverrides: OverrideFactory,
  ): GetTheme =>
  (getThemeOptions = {}) => {
    const { options = {}, overrides = {} } = getThemeOptions

    const theme = createTheme(
      mergeDeepRight(defaultThemeOptions, options) as ThemeOptions,
    )

    theme.overrides = mergeDeepRight(
      defaultOverrides(theme),
      typeof overrides === 'function' ? overrides(theme) : overrides,
    ) as Overrides

    return theme
  }

export enum TenantUuid {
  Smava = 'f9343d8a-4bd3-48b8-a504-e99f102f463c',
  FinanzcheckShared = '2245defb-94aa-4c24-9bb1-ce9c06e097f8',
  FinanzcheckStage = '744e3a15-6238-4c2e-9c39-8411cf2b6a1b',
  FinanzcheckLive = 'de0b41bd-175b-4b13-adf8-eef7450bf698',
}

const getThemeForTenant: Record<
  TenantUuid,
  () => Promise<{ getTheme: GetTheme }>
> = {
  [TenantUuid.Smava]: () => import('./theme/smava'),
  [TenantUuid.FinanzcheckShared]: () => import('./theme/finanzcheck'),
  [TenantUuid.FinanzcheckStage]: () => import('./theme/finanzcheck'),
  [TenantUuid.FinanzcheckLive]: () => import('./theme/finanzcheck'),
}

export async function getTheme(
  tenantUuid: TenantUuid,
  themeOptions: GetThemeOptions = {},
) {
  const themeImport = getThemeForTenant[tenantUuid]
  if (!themeImport) {
    throw new UnknownTenantUuidError(tenantUuid)
  }

  return (await themeImport()).getTheme(themeOptions)
}
